import cloneDeep from 'lodash.clonedeep';

import type { GenericObject, PaymentTerm } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import {
    FETCH_QUESTIONS_OR_PAYMENT,
    INIT_PENCIL_SUCCESS,
    LOCATION_ID_UPDATED,
    PAYMENT_FAILURE,
    PAYMENT_RECEIVED,
    PAYMENT_REQUESTED,
    PROGRAMS_END_DAYS,
    SYNC_MESSAGE,
    UPDATED_DAYS_TO_FIRST_PAYMENT,
    UPDATED_USE_CALCULATED_GOV_FEES,
    UPDATED_OFFER_MENU_PRODUCTS,
    UPDATED_SHOPPER_ZIP,
    ZIP_CODE_PAYMENT_FAILED,
    UPDATE_CONTRACT_DATE,
    PAYMENT_ABORTED,
    PAYMENT_FETCH_STARTED,
    PAYMENT_FETCH_COMPLETED
} from '../actions/paymentActionTypes';

import {
    APPLIED_PROGRAM,
    CLEAR_PAYMENT_GRID_TERM_PROGRAMS,
    CLOSE_LENDER_CHANGED_BANNER,
    CREDIT_APP_ZIP_CODE_UPDATED,
    HONOR_LEASE_MILEAGE_CHANGE,
    LOCATION_RESET_OVERRIDES,
    RESET_TAX_OVERRIDES,
    RESTORE_PAYMENT_GRID_PROGRAMS,
    UPDATED_SELL_RATE_OVERRIDE,
    UPDATED_SHOPPER_CONTACT,
    UPDATED_SHOPPER_CONTACT_RETRY,
    CLEAR_TAX_AMOUNTS
} from '../types/actionTypes';
import type {
    AppliedProgramInfo,
    PaymentErrorCategory,
    PaymentState,
    TProgramInfoAll,
    TProgramInfoByTerm
} from '../types/paymentTypes';
import { getCalculationSettings, getIncludeGovernmentFees, isTaxesIncluded } from '../selectors/paymentSelectors';
import { reformatQuestions } from '../utils/paymentPayloadUtils';
import { getLoadPayloadFeatureToggles } from '../utils/loadPayloadFeatureToggles';
import { keepLockedProgramInUpqCache, markLoadedUpqLockKeptTerms, markUpqLockKeptTerms } from '../utils/explicitProgramLockUtils';
import { buildAppliedProgramInfo, isUserPricedProgram, isValidProgram } from '../utils/lenderProgramUtils';
import { dateUtils } from '@makemydeal/dr-common-utils';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { AnyAction } from 'redux';

export interface IAnswer {
    id: string;
    answer: string;
    question: string;
    financeType: string;
}

const initialState: PaymentState = {
    isCalculating: false,
    isFetching: false,
    error: false,
    isLenderChanged: false,
    shopperUpdateFailed: false,
    includeTaxes: false,
    includeGovernmentFees: false,
    locationId: '',
    '@@dr_payment': true,
    programInfoByTerm: {
        finance: {},
        lease: {},
        balloon: {}
    }
};

function reducer(state = initialState, action: AnyAction & AnyFSA) {
    switch (action.type) {
        case LOCATION_ID_UPDATED: {
            const newState = { ...state };
            newState.locationId = action?.payload?.selectedLocationId;
            newState.previousLocationId = state.locationId;
            return newState;
        }
        case FETCH_QUESTIONS_OR_PAYMENT: {
            const newState: Partial<PaymentState> = {
                zipCode: action?.payload?.zipCode,
                includeTaxes: true,
                locationId: ''
            };
            return { ...state, ...newState };
        }
        case UPDATED_SHOPPER_CONTACT: {
            const newState: Partial<PaymentState> = { ...state };
            newState.zipCode = action.payload.zip;
            return newState;
        }
        case PROGRAMS_END_DAYS:
            return {
                ...state,
                programEndDays: action.payload
            };

        case PAYMENT_REQUESTED: {
            const { gridAction = false, isMainPayment = false } = action.meta?.originalAction?.meta?.paymentGrid || {};

            if (gridAction && !isMainPayment) {
                return {
                    ...state,
                    traceId: action.payload?.traceId,
                    actionForPayment: action?.payload?.actionType
                };
            }

            return {
                ...state,
                isCalculating: true,
                error: false,
                zipCode: action?.payload && 'zipCode' in action.payload ? action.payload.zipCode : state.zipCode,
                actionForPayment: action?.payload?.actionType,
                traceId: action?.payload?.traceId,
                termStrategy: action?.meta?.termStrategy || action?.meta?.originalAction?.meta?.termStrategy
            };
        }

        case PAYMENT_ABORTED: {
            return {
                ...state,
                isCalculating: false,
                isFetching: false,
                isAwaitingFreshPaymentResponse: false
            };
        }

        case PAYMENT_FETCH_STARTED: {
            return {
                ...state,
                isFetching: true
            };
        }

        case PAYMENT_FETCH_COMPLETED: {
            return {
                ...state,
                isFetching: false
            };
        }

        case PAYMENT_RECEIVED: {
            // R-10 latest-call-wins: a superseded (stale) response must not apply its result (program info,
            // trace/action metadata, lastPaymentMissingUPQFields) — the fresh call owns that. We still clear
            // the calculating/fetching spinner so the UI can't get stuck if the fresh call never lands. Only
            // leaseEndpointMiddleware sets this flag.
            const {
                gridAction = false,
                isMainPayment = false,
                skipProgramInfoUpdate = false
            } = action.meta?.originalAction?.meta?.paymentGrid || {};
            if (action.meta?.isStalePaymentResponse) {
                return {
                    ...state,
                    isCalculating: false,
                    isFetching: false,
                    isAwaitingFreshPaymentResponse: !(gridAction && !isMainPayment) || state.isAwaitingFreshPaymentResponse
                };
            }
            if (gridAction && !isMainPayment) {
                // A per-row grid payment must not repin its column's program: `updateProgramInfo` merges per term and
                // never clears, so whichever row answered last would decide it. This branch writes nothing else.
                if (skipProgramInfoUpdate) {
                    return state;
                }
                return {
                    ...state,
                    programInfoByTerm: updateProgramInfo(action.payload, state.programInfoByTerm)
                };
            }

            /**
             * A Lease grid call forced onto user-program-quotes must not write back a program: its response carries a
             * *synthesised* user program that passes `isValidProgram`, so merging it would make the column read as
             * program-backed again and undo the user's own rate. Finance relies on the merge and its own stickiness.
             */
            const originalActionMeta = action.meta?.originalAction?.meta;

            const isForcedLeaseGridCall =
                originalActionMeta?.forceUseGenericCalcs === true &&
                (gridAction
                    ? (originalActionMeta?.middleware?.offerType ?? action.meta?.offerType) === LEASE
                    : // A grid CELL selection is a bare SELECTED_TERM with no `meta.paymentGrid` to read the offer type
                      // from, so `forceEngineForSelectedCell` stamps it where the click is handled.
                      originalActionMeta?.gridCellSelectionOfferType === LEASE);

            const newState = {
                ...state,
                isCalculating: false,
                isFetching: false,
                isAwaitingFreshPaymentResponse: false,
                error: false,
                // Cleared on success so the category can't outlive the failure it described. Readers still
                // have to pair it with `error`: a failure that doesn't write this (ZIP_CODE_PAYMENT_FAILED
                // leaves both this and `actionForPayment` alone) will still read as the previous failure.
                errorCategory: undefined as PaymentErrorCategory | undefined,
                lastSuccessfulZipCode: action.payload && 'zipCode' in action.payload ? action.payload.zipCode : state.zipCode,
                traceId: action.payload?.traceId,
                actionForPayment: action.payload?.actionForPayment,
                programInfoByTerm: isForcedLeaseGridCall
                    ? state.programInfoByTerm
                    : keepLockedProgramInUpqCache(updateProgramInfo(action.payload, state.programInfoByTerm), action),
                upqLockKeptTerms: markUpqLockKeptTerms(state.upqLockKeptTerms, action),
                lastPaymentMissingUPQFields: false
            };

            // if the shopper zip is changed, FETCH_QUESTIONS_OR_PAYMENT is fired
            // we should only update the questionsList, locationId, city and county names whenever we change the zipcode
            if (action.meta.originalAction.type === FETCH_QUESTIONS_OR_PAYMENT) {
                if (action.payload?.questions) {
                    newState.questionsList = reformatQuestions(action.payload.questions);
                    newState.locationId = action.payload.questions.find(
                        ({ id }: { id: any }) => id === 'LocationID'
                    )?.options[0]?.value;
                } else {
                    delete newState.questionsList;
                    delete newState.locationId;
                }
            }
            if (action.meta.originalAction.type === UPDATED_SHOPPER_CONTACT_RETRY) {
                newState.isLenderChanged = true;
            }

            return newState;
        }
        case UPDATED_SHOPPER_ZIP: {
            return { ...state, zipCode: action.payload };
        }
        case PAYMENT_FAILURE: {
            const { gridAction = false, isMainPayment = false } = action.meta?.originalAction?.meta?.paymentGrid || {};
            if (gridAction && !isMainPayment) {
                return state;
            }

            return {
                ...state,
                isCalculating: false,
                isFetching: false,
                isAwaitingFreshPaymentResponse: false,
                error: true,
                errorMessages: action.meta?.error?.errors,
                errorCategory: action.meta?.error?.errorCategory,
                shopperUpdateFailed: action.meta?.shopperUpdateFailed,
                traceId: action.meta?.traceId,
                actionForPayment: action.meta?.actionForPayment,
                lastPaymentMissingUPQFields: action.meta?.originalAction?.meta?.missingRequiredUPQFields ?? false
            };
        }

        case ZIP_CODE_PAYMENT_FAILED:
            return {
                ...state,
                isCalculating: false,
                isFetching: false,
                isAwaitingFreshPaymentResponse: false,
                error: true,
                lastFailedZipCode: action.payload && 'zipCode' in action.payload ? action.payload.zipCode : state.zipCode
            };

        case SYNC_MESSAGE:
            return { ...state, ...action.message.payment };

        case UPDATED_OFFER_MENU_PRODUCTS:
            return { ...state, includeTaxes: action.payload.includeTaxes === undefined ? true : action.payload.includeTaxes };

        case CLEAR_TAX_AMOUNTS:
            // Also turn off the include-default-taxes calc setting so the cleared state survives save/reload
            // (see INIT_PENCIL_SUCCESS below) and the next PO call does not recompute default taxes
            // (getFeesAndTaxesSettings -> includeDefaultTaxes in paymentPayloadUtils).
            return { ...state, includeTaxes: false, useDealerDefaultTaxes: false };

        case RESET_TAX_OVERRIDES:
            // Un-clear: restore default-tax calculation so taxes recompute again.
            return { ...state, includeTaxes: true, useDealerDefaultTaxes: true };

        case INIT_PENCIL_SUCCESS: {
            const dealerZip = action?.payload?.dealer?.address?.zip;
            const dealSetupTaxCallMade = action?.payload?.offer?.dealSetupTaxCallMade;
            const shopperZip = action?.payload?.offer.shopper?.zip;
            const zip = action?.payload?.payment?.zipCode;
            const dealer = action?.payload?.dealer;
            const calculationDetails = action?.payload?.calculationDetails;
            const daysToFirstPayment = action?.payload?.offer?.payment?.daysToFirstPayment;
            const dealerTaxAddress = action?.payload?.dealer?.dealerTaxAddress;
            const paymentsCalculated = action?.payload?.meta?.paymentsCalculated;
            const includeGovernmentFees = getIncludeGovernmentFees(dealer);
            const calculationSettings = getCalculationSettings(calculationDetails);
            // Honor a persisted "taxes cleared" intent: when the saved deal carries
            // useDealerDefaultTaxes === false, keep taxes off on reload instead of re-deriving true from the
            // dealer/MV setting (isTaxesIncluded). Without this the dealer condition would flip includeTaxes
            // back to true and default taxes would recompute.
            const includeTaxes = calculationSettings.useDealerDefaultTaxes === false ? false : isTaxesIncluded(dealer);

            const contractSignedDate = action?.payload?.dealLifecycle?.contractSignedDate;
            const isDealerTimeZoneForContractDateEnabled = getLoadPayloadFeatureToggles(
                action?.payload
            )?.useDealerTimeZoneForContractDate_DE752874;
            const isBaseExperience = action?.payload?.dealer?.dealScreenExperience === 'base';

            let contractDate: string | undefined;
            if (isDealerTimeZoneForContractDateEnabled) {
                // Toggle on: forward the raw signed instant; the payment service resolves it in the dealer's timezone.
                contractDate = contractSignedDate || undefined;
            } else if (isBaseExperience && contractSignedDate) {
                // Toggle off (legacy): only the base experience sends a contract date, derived in UTC.
                contractDate = dateUtils.formatUtcDateTimeToDateOnly(contractSignedDate);
            }
            // Toggle off + non-base (or no signed date): no contract date sent — unchanged legacy behavior.

            const newState = {
                zipCode: zip || shopperZip,
                lastSuccessfulZipCode: zip || shopperZip || (dealSetupTaxCallMade && dealerZip) || '',
                locationId: action.payload?.offer?.locationId || '',
                registrationCityName: action.payload?.offer?.registrationCityName || '',
                registrationCountyName: action.payload?.offer?.registrationCountyName || '',
                includeTaxes,
                includeGovernmentFees,
                paymentsCalculated,
                programInfoByTerm: updateProgramInfo(action.payload.offer),
                upqLockKeptTerms: markLoadedUpqLockKeptTerms(action.payload),
                ...calculationSettings,
                ...(daysToFirstPayment && { daysToFirstPayment }),
                ...(contractDate && { contractDate }),
                ...(dealerTaxAddress && { dealerTaxAddress: { ...dealerTaxAddress } })
            };

            return {
                ...state,
                ...newState
            };
        }
        case APPLIED_PROGRAM: {
            const currentOfferType = action.meta.currentOfferType;
            if (currentOfferType == CASH) {
                return state;
            }
            const programData = action.payload.programData;
            const months = programData.term;
            const newState = {
                ...state,
                programInfoByTerm: {
                    ...state.programInfoByTerm,
                    [currentOfferType]: {
                        ...state.programInfoByTerm[currentOfferType],
                        [months]: buildAppliedProgramInfo(state.programInfoByTerm[currentOfferType][months], programData)
                    }
                }
            };
            return newState;
        }
        case LOCATION_RESET_OVERRIDES:
        case HONOR_LEASE_MILEAGE_CHANGE: {
            const currentOfferType = action.meta.currentOfferType;
            if (action.type === HONOR_LEASE_MILEAGE_CHANGE && currentOfferType !== LEASE) return state;
            const offerType = action.meta.currentOfferType || LEASE;
            const currentTermMonth = action.meta.currentTermMonth;
            return {
                ...state,
                programInfoByTerm: {
                    ...state.programInfoByTerm,
                    [offerType]: {
                        [currentTermMonth]: {}
                    }
                }
            };
        }
        case CLEAR_PAYMENT_GRID_TERM_PROGRAMS: {
            const { offerType, termMonths } = action.payload || {};
            if (!offerType || !termMonths?.length) return state;

            const emptiedTerms = (termMonths as number[]).reduce<GenericObject>((terms, months) => {
                terms[months] = {};
                return terms;
            }, {});

            return {
                ...state,
                programInfoByTerm: {
                    ...state.programInfoByTerm,
                    [offerType]: {
                        ...state.programInfoByTerm[offerType],
                        ...emptiedTerms
                    }
                }
            };
        }
        case CREDIT_APP_ZIP_CODE_UPDATED: {
            const { month, zipCode } = action.meta;
            if (zipCode && zipCode !== state.zipCode) {
                return {
                    ...state,
                    zipCode,
                    programInfoByTerm: {
                        ...state.programInfoByTerm,
                        [FINANCE]: {
                            ...state.programInfoByTerm[FINANCE],
                            [month]: {}
                        }
                    }
                };
            }
            return state;
        }
        case CLOSE_LENDER_CHANGED_BANNER: {
            const newState = { ...state };
            newState.isLenderChanged = action?.payload?.isLenderChanged;
            newState.shopperUpdateFailed = false;
            return newState;
        }
        case UPDATED_DAYS_TO_FIRST_PAYMENT: {
            const newState = { ...state };
            newState.daysToFirstPayment = action.payload;
            return newState;
        }
        case UPDATE_CONTRACT_DATE: {
            const newState = { ...state };
            newState.contractDate = action.payload;
            return newState;
        }

        case UPDATED_USE_CALCULATED_GOV_FEES: {
            const newState = { ...state };
            newState.useCalculatedGovFees = action.payload;
            return newState;
        }

        case RESTORE_PAYMENT_GRID_PROGRAMS: {
            const { offerType: restoredOfferType, termRecords } = action.payload as {
                offerType: keyof TProgramInfoAll;
                termRecords: PaymentTerm[];
            };
            const cached: TProgramInfoByTerm = state.programInfoByTerm[restoredOfferType] ?? {};
            const restored = termRecords.reduce<TProgramInfoByTerm>((byMonths, record) => {
                const { months, lenderProgram } = record;
                if (months == null || byMonths[months] || isValidProgram(cached[months])) return byMonths;
                if (!isValidProgram(lenderProgram) || isUserPricedProgram(lenderProgram)) return byMonths;
                byMonths[months] = buildAppliedProgramInfo(undefined, record) as AppliedProgramInfo;
                return byMonths;
            }, {});
            if (!Object.keys(restored).length) return state;
            return {
                ...state,
                programInfoByTerm: { ...state.programInfoByTerm, [restoredOfferType]: { ...cached, ...restored } }
            };
        }
        case UPDATED_SELL_RATE_OVERRIDE: {
            const { months, program } = action.payload;

            // We're only updating the program
            if (!program) {
                return state;
            }

            const sellRateProgramUpdate = {
                [LEASE]: {},
                terms: {
                    leaseTerms: ['_update_sell_rate_term'],
                    byId: {
                        _update_sell_rate_term: {
                            months,
                            lenderProgram: program
                        }
                    }
                }
            };
            return {
                ...state,
                programInfoByTerm: updateProgramInfo(sellRateProgramUpdate, state.programInfoByTerm)
            };
        }
        default:
            return state;
    }
}

const getProgramInfoByTerm = (termsById: GenericObject<PaymentTerm>, termIdList: string[]): TProgramInfoByTerm => {
    const programInfoByTerm: GenericObject = {};
    termIdList?.forEach((termId) => {
        const term = termsById[termId];
        programInfoByTerm[term.months] = buildAppliedProgramInfo(undefined, term);
    });
    return programInfoByTerm;
};

const updateProgramInfo = (offerNode: any, prevState?: TProgramInfoAll): TProgramInfoAll => {
    const terms = offerNode?.terms || {};
    const programInfo = prevState ? cloneDeep(prevState) : ({} as TProgramInfoAll);

    if (offerNode?.[FINANCE]) {
        programInfo[FINANCE] = {
            ...programInfo[FINANCE],
            ...getProgramInfoByTerm(terms.byId, terms.financeTerms)
        };
    }

    if (offerNode?.[LEASE]) {
        programInfo[LEASE] = {
            ...programInfo[LEASE],
            ...getProgramInfoByTerm(terms.byId, terms.leaseTerms)
        };
    }

    if (offerNode?.[BALLOON]) {
        programInfo[BALLOON] = {
            ...programInfo[BALLOON],
            ...getProgramInfoByTerm(terms.byId, terms.balloonTerms)
        };
    }

    return programInfo;
};

export default reducer;
